import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from "react";
import moment from "moment";
import { Button } from "primereact/button";
import { ToastService } from "../../../AppToast";
import { DateTimeRange } from "../../../shared/component/DateTimeRange";
import { PaymentLogService } from "../PaymentLogService";
import { useDataTable } from "../../../shared/hooks/DataTable";
import { i18n, useTranslation } from "../../../shared/language";
import { useImExExcel } from "../../../shared/hooks/ImExExcel";
import { InvoiceService } from "../../hoa-don/InvoiceService";
import { DialogView } from "../../hoa-don/component/DialogView";
import { DialogQrCode } from "../../hoa-don/component/DialogQrCode";
import {
  fetchPetrolQrPaymentEnabled,
  PETROL_QR_PAYMENT_NOT_REGISTERED_MESSAGE,
} from "../../hoa-don/hooks/petrolQrPaymentSetting";
import {
  DATA_SOURCE_OPTIONS,
  DEFAULT_DATA_SOURCE_VALUES,
  DataSourceValue,
  DATE_TIME_FORMAT,
  ISO_DATE_TIME_FORMAT,
  PAYMENT_STATUS,
  PAYMENT_STATUS_OPTIONS,
  PaymentStatusFilter,
  QR_TIME_PRESETS,
  QrTimePreset,
} from "./constants";

const formatAmountVnd = (value: unknown) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numberValue =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value)
        : NaN;

  if (!Number.isFinite(numberValue)) {
    return String(value);
  }

  return new Intl.NumberFormat("vi-VN").format(numberValue);
};

const formatDateTimeSpec = (value: unknown) => {
  if (!value) {
    return "";
  }

  return moment(value).format(DATE_TIME_FORMAT);
};

const getDateRangeByPreset = (
  preset: QrTimePreset,
  customRange: string | null,
) => {
  if (preset === "CUSTOM") {
    const range = mapDateTimeRange(customRange, "fromDate", "toDate");
    if (!range.fromDate || !range.toDate) {
      return null;
    }

    return {
      fromDate: range.fromDate,
      toDate: range.toDate,
    };
  }

  const presetConfig = QR_TIME_PRESETS.find((item) => item.value === preset);
  if (!presetConfig || presetConfig.days === null) {
    return null;
  }

  const toDate = moment().endOf("day");
  const fromDate =
    preset === "TODAY"
      ? moment().startOf("day")
      // Tính cả ngày hiện tại nên mốc bắt đầu phải lùi ít hơn một ngày so với số ngày hiển thị.
      : moment().subtract(presetConfig.days - 1, "days").startOf("day");

  return {
    fromDate: fromDate.format(ISO_DATE_TIME_FORMAT),
    toDate: toDate.format(ISO_DATE_TIME_FORMAT),
  };
};

const PAYMENT_STATUS_BADGE: Record<
  number,
  { translationKey: string; className: string; style?: CSSProperties }
> = {
  [PAYMENT_STATUS.UNPAID]: {
    translationKey: "common.unpaid",
    className: "invoice-status-5",
  },
  [PAYMENT_STATUS.PAID]: {
    translationKey: "common.paymentFull",
    className: "invoice-status-0",
  },
  [PAYMENT_STATUS.UNDERPAID]: {
    translationKey: "common.paymentShort",
    className: "invoice-status",
    style: {
      backgroundColor: "#d4edf7",
      color: "#0069b4",
    },
  },
  [PAYMENT_STATUS.OVERPAID]: {
    translationKey: "common.paymentExcess",
    className: "invoice-status-4",
  },
};

type LazyFilterMeta = {
  value?: unknown;
  matchMode?: string;
};

const hasFilterValue = (value: unknown): boolean => {
  if (value === null || value === undefined || value === "") {
    return false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return true;
};

const ARRAY_FILTER_FIELDS = new Set(["sourceType"]);

const extractColumnFilterParams = (
  filters: Record<string, LazyFilterMeta> | undefined,
): Record<string, unknown> => {
  if (!filters) {
    return {};
  }

  const result: Record<string, unknown> = {};

  Object.keys(filters).forEach((key) => {
    if (ARRAY_FILTER_FIELDS.has(key)) {
      return;
    }

    const filter = filters[key];
    if (!hasFilterValue(filter?.value)) {
      return;
    }

    if (Array.isArray(filter.value)) {
      result[key] = filter.value.join(",");
      return;
    }

    result[key] = filter.value;
  });

  return result;
};

const mapDateTimeRange = (
  value: unknown,
  fromKey: string,
  toKey: string,
): Record<string, string> => {
  if (typeof value !== "string" || !value.includes(" - ")) {
    return {};
  }

  const [from, to] = value.split(" - ");

  return {
    [fromKey]: moment(from, DATE_TIME_FORMAT).format(ISO_DATE_TIME_FORMAT),
    [toKey]: moment(to, DATE_TIME_FORMAT).format(ISO_DATE_TIME_FORMAT),
  };
};

const isSourceTypeFilterEmpty = (lazyParams?: {
  filters?: Record<string, LazyFilterMeta>;
}): boolean => {
  const value = lazyParams?.filters?.sourceType?.value;
  return Array.isArray(value) && value.length === 0;
};

const normalizeSourceType = (value: unknown): DataSourceValue[] => {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is DataSourceValue =>
        typeof item === "string" && item.length > 0,
    );
  }

  if (typeof value === "string" && value.length > 0) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter((item): item is DataSourceValue => item.length > 0);
  }

  return [];
};

const resolveSourceTypeParam = (
  lazyParams?: { filters?: Record<string, LazyFilterMeta> },
): DataSourceValue[] => {
  if (isSourceTypeFilterEmpty(lazyParams)) {
    return [];
  }

  const filterValue = lazyParams?.filters?.sourceType?.value;
  if (hasFilterValue(filterValue)) {
    return normalizeSourceType(filterValue);
  }

  return [...DEFAULT_DATA_SOURCE_VALUES];
};

const buildPaymentLogApiParams = (
  params: Record<string, unknown>,
  lazyParams?: { filters?: Record<string, LazyFilterMeta> },
): Record<string, unknown> => {
  const columnFilters = extractColumnFilterParams(lazyParams?.filters);
  const mergedParams = { ...params, ...columnFilters };
  const {
    creationTime,
    paymentTime,
    lastModificationTime,
    sourceType: _sourceType,
    ...rest
  } = mergedParams;

  const apiParams: Record<string, unknown> = { ...rest };

  apiParams.sourceType = resolveSourceTypeParam(lazyParams);

  if (hasFilterValue(creationTime)) {
    Object.assign(
      apiParams,
      mapDateTimeRange(creationTime, "fromDate", "toDate"),
    );
  }

  if (hasFilterValue(paymentTime)) {
    Object.assign(
      apiParams,
      mapDateTimeRange(paymentTime, "fromPaymentDate", "toPaymentDate"),
    );
  }

  if (hasFilterValue(lastModificationTime)) {
    Object.assign(
      apiParams,
      mapDateTimeRange(
        lastModificationTime,
        "fromModificationDate",
        "toModificationDate",
      ),
    );
  }

  if (!apiParams.sorting) {
    apiParams.sorting = "creationTime";
    apiParams.sortType = "DESCEND";
  }

  return apiParams;
};

const EMPTY_PAYMENT_LOG_LIST = Promise.resolve({ items: [], totalCount: 0 });

const omitPaginationParams = (
  params: Record<string, unknown>,
): Record<string, unknown> => {
  const { skipCount, maxResultCount, ...rest } = params;
  return rest;
};

const renderPaymentStatus = (status: number | null | undefined) => {
  const current =
    status !== null && status !== undefined
      ? PAYMENT_STATUS_BADGE[status]
      : undefined;
  if (!current) {
    return null;
  }

  return (
    <span
      className={`invoice-status ${current.className}`}
      style={current.style}
    >
      {i18n.t(current.translationKey)}
    </span>
  );
};

type PaymentLogRow = {
  sourceType?: DataSourceValue;
  resourceId?: string;
};

const getSourceTypeLabel = (value: unknown) => {
  const option = DATA_SOURCE_OPTIONS.find((item) => item.value === value);
  return option ? i18n.t(option.translationKey) : "";
};

const LichSuThanhToan = () => {
  const { t } = useTranslation();
  const header = t("common.qrCodePayment");
  const dataKey = "id";

  const [paymentStatus, setPaymentStatus] = useState<PaymentStatusFilter>(
    PAYMENT_STATUS.ALL,
  );
  const [timePreset, setTimePreset] = useState<QrTimePreset>("30DAYS");
  const [customDateTimeRange, setCustomDateTimeRange] = useState("");
  const [requiredParams, setRequiredParams] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [displayView, setDisplayView] = useState(false);
  const [viewItem, setViewItem] = useState<Record<string, unknown> | null>(
    null,
  );
  const [displayQrCode, setDisplayQrCode] = useState(false);
  const [qrCodeItem, setQrCodeItem] = useState<{ id: string } | null>(null);

  const handleSourceTypeClick = useCallback((rowData: PaymentLogRow) => {
    const { resourceId, sourceType } = rowData;

    if (sourceType === "invoice") {
      if (!resourceId) {
        ToastService.error(t("common.invoiceInfoNotFound"));
        return;
      }

      InvoiceService.getDetail(resourceId)
        .then((data) => {
          setViewItem(data);
          setDisplayView(true);
        })
        .catch(() => {
          ToastService.error(t("common.invoiceInfoLoadFailed"));
        });
      return;
    }

    if (sourceType === "fuel_log") {
      if (!resourceId) {
        ToastService.error(t("common.pumpLogInfoNotFound"));
        return;
      }

      fetchPetrolQrPaymentEnabled().then((isQrPaymentEnabled) => {
        if (!isQrPaymentEnabled) {
          ToastService.error(PETROL_QR_PAYMENT_NOT_REGISTERED_MESSAGE);
          return;
        }

        setQrCodeItem({ id: resourceId });
        setDisplayQrCode(true);
      });
    }
  }, []);

  const buildFilterParams = useCallback((): Record<string, unknown> | null => {
    const dateRange = getDateRangeByPreset(timePreset, customDateTimeRange);
    if (!dateRange) {
      return null;
    }

    return {
      ...dateRange,
      ...(paymentStatus !== PAYMENT_STATUS.ALL ? { paymentStatus } : {}),
    };
  }, [customDateTimeRange, paymentStatus, timePreset]);

  useEffect(() => {
    setRequiredParams(buildFilterParams());
  }, [buildFilterParams]);

  const {
    render: renderDataTable,
    reloadLazyData,
    dataTableParams,
    lazyParams,
  } = useDataTable({
    header,
    dataKey,
    indexColumnWidth: 50,
    requiredParams,
    columns: [
      {
        field: "sourceType",
        header: t("common.sourceData"),
        width: 130,
        matchMode: "in",
        filterType: "multiselect",
        filterOptions: [...DATA_SOURCE_OPTIONS],
        filterDefaultValue: [...DEFAULT_DATA_SOURCE_VALUES],
        dataType: "custom",
        customCell(rowData: PaymentLogRow) {
          const label = getSourceTypeLabel(rowData.sourceType);
          if (!label) {
            return null;
          }

          if (
            rowData.sourceType !== "invoice" &&
            rowData.sourceType !== "fuel_log"
          ) {
            return label;
          }

          return (
            <span
              className="text-blue-500 underline cursor-pointer"
              onClick={() => handleSourceTypeClick(rowData)}
            >
              {label}
            </span>
          );
        },
      },
      {
        field: "bankAccount",
        header: t("catalog.beneficiaryBank"),
        width: 170,
        matchMode: "contains",
      },
      {
        field: "amount",
        header: t("common.qrCreatedAmount"),
        width: 150,
        matchMode: "equals",
        filterType: "number",
        dataType: "custom",
        style: { justifyContent: "end" },
        customCell(rowData) {
          return (
            <span className="block text-right w-full">
              {formatAmountVnd(rowData.amount)}
            </span>
          );
        },
      },
      {
        field: "paidAmount",
        header: t("common.paidTotal"),
        width: 170,
        matchMode: "equals",
        filterType: "number",
        dataType: "custom",
        style: { justifyContent: "end" },
        customCell(rowData) {
          return (
            <span className="block text-right w-full">
              {formatAmountVnd(rowData.paidAmount)}
            </span>
          );
        },
      },
      {
        field: "paymentStatus",
        header: t("common.paymentStatus"),
        width: 170,
        // matchMode: "equals",
        filterType: "dropdown",
        filterOptions: PAYMENT_STATUS_OPTIONS.filter(
          (item) => item.value !== PAYMENT_STATUS.ALL,
        ).map((item) => ({
          value: item.value,
          label: t(item.translationKey),
        })),
        dataType: "custom",
        style: { justifyContent: "center" },
        customCell(rowData) {
          return renderPaymentStatus(rowData.paymentStatus);
        },
      },
      {
        field: "creationTime",
        header: t("common.qrCreationTime"),
        // matchMode: "equals",
        filterType: "date-time-range",
        dataType: "custom",
        width: 160,
        customCell(rowData) {
          return formatDateTimeSpec(rowData.creationTime);
        },
      },
      {
        field: "paymentTime",
        header: t("common.paymentTime"),
        width: 180,
        matchMode: "equals",
        filterType: "date-time-range",
        dataType: "custom",
        customCell(rowData) {
          return formatDateTimeSpec(rowData.paymentTime);
        },
      },
      {
        field: "paymentContent",
        header: t("common.content"),
        minWidth: 220,
        matchMode: "contains",
      },
      {
        field: "transactionId",
        header: t("common.transactionId"),
        width: 180,
        matchMode: "contains",
      },
      {
        field: "requestId",
        header: t("common.requestCode"),
        width: 200,
        matchMode: "contains",
      },
      {
        field: "refNo",
        header: t("common.bankTransactionCode"),
        width: 180,
        matchMode: "contains",
      },
      {
        field: "invoiceSerial",
        header: t("common.symbol"),
        width: 120,
        matchMode: "contains",
      },
      {
        field: "invoiceNumber",
        header: t("common.invoiceNo"),
        width: 120,
        matchMode: "contains",
        style: { justifyContent: "end" },
      },
      {
        field: "lastModificationTime",
        header: t("common.lastModificationTime"),
        width: 180,
        matchMode: "equals",
        filterType: "date-time-range",
        dataType: "custom",
        customCell(rowData) {
          return formatDateTimeSpec(rowData.lastModificationTime);
        },
      },
    ],
    getList: (params, lazyParams) => {
      if (isSourceTypeFilterEmpty(lazyParams)) {
        return EMPTY_PAYMENT_LOG_LIST;
      }

      return PaymentLogService.getList(
        buildPaymentLogApiParams(params, lazyParams),
      );
    },
  });

  const { download } = useImExExcel({
    exportTemplate: () => PaymentLogService.downloadExcelTemplate(),
    exportExcel: (params) => PaymentLogService.downloadExcel(params),
    exportExcelFileName: "lich-su-thanh-toan-qrcode",
    importExcel: () => Promise.resolve(),
    reloadLazyData,
  });

  const statusButtons = useMemo(() => {
    return PAYMENT_STATUS_OPTIONS.map((item) => (
      <Button
        key={item.translationKey}
        label={t(item.translationKey)}
        className={`p-button-sm mr-2 mb-2 ${paymentStatus === item.value ? "" : "p-button-outlined"}`}
        onClick={() => setPaymentStatus(item.value)}
      />
    ));
  }, [paymentStatus, t]);

  const timeButtons = useMemo(() => {
    return QR_TIME_PRESETS.map((item) => (
      <Button
        key={item.value}
        label={t(item.translationKey)}
        className={`p-button-sm mr-2 mb-2 ${timePreset === item.value ? "" : "p-button-outlined"}`}
        onClick={() => setTimePreset(item.value)}
      />
    ));
  }, [timePreset, t]);

  const handleClearFilter = () => {
    setPaymentStatus(PAYMENT_STATUS.ALL);
    setTimePreset("30DAYS");
    setCustomDateTimeRange("");
    reloadLazyData();
  };

  const handleExportExcel = () => {
    if (isSourceTypeFilterEmpty(lazyParams)) {
      ToastService.error(t("common.selectDataSource"));
      return;
    }

    const exportParams = omitPaginationParams(
      buildPaymentLogApiParams(
        dataTableParams as Record<string, unknown>,
        lazyParams,
      ),
    );

    if (!exportParams.fromDate || !exportParams.toDate) {
      ToastService.error(t("common.selectQrCreationPeriod"));
      return;
    }

    download(exportParams);
  };

  return (
    <Fragment>
      <div
        className="flex flex-column gap-2"
        style={{ height: "calc(100vh - 9rem)" }}
      >
        <div className="surface-card border-round p-3 flex-shrink-0">
          <div className="mb-3 flex gap-2 align-items-center">
            <div className="font-medium mb-2 font-semibold w-10rem">
              {t("common.paymentStatus")}
            </div>
            <div className="flex flex-wrap">{statusButtons}</div>
          </div>

          <div className="flex gap-2 align-items-center justify-content-between">
            <div className="flex gap-2 align-items-center">
              <div className="font-medium mb-2 font-semibold w-10rem">
                {t("common.qrCreationTime")}
              </div>
              <div className="flex flex-wrap">
                <div>
                  {timeButtons}
                  {timePreset === "CUSTOM" && (
                    <div className="inline-block mb-1 mr-2">
                      <DateTimeRange
                        type="date-time-range"
                        value={customDateTimeRange}
                        onChange={setCustomDateTimeRange}
                      />
                    </div>
                  )}
                  <Button
                    label={t("common.clear")}
                    icon="pi pi-times"
                    className="p-button-outlined p-button-danger p-button-sm mb-2"
                    onClick={handleClearFilter}
                  />
                  <Button
                    label={t("common.refresh")}
                    icon="pi pi-refresh"
                    className="p-button-outlined p-button-sm mb-2 ml-2 border-1 border-gray-300 text-gray-500"
                    onClick={() => reloadLazyData()}
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                label={t("common.exportExcel")}
                icon="pi pi-file-excel"
                className="p-button-outlined p-button-sm mb-2"
                onClick={handleExportExcel}
              />
            </div>
          </div>
        </div>

        <div className="surface-card border-round flex-1 min-h-0 overflow-hidden payment-log-table-host">
          {renderDataTable()}
        </div>
      </div>

      <DialogView
        display={displayView}
        setDisplay={setDisplayView}
        selectedItem={viewItem}
        setSelectedItems={setViewItem}
        showMinimalToolbar
      />
      <DialogQrCode
        display={displayQrCode}
        setDisplay={setDisplayQrCode}
        selectedItems={qrCodeItem}
      />
    </Fragment>
  );
};

export default LichSuThanhToan;
